(ns web3.persistence.graphql
  (:require-macros [web3.build.versions :as versions])
  (:require ["apollo-client" :refer [ApolloClient]]
            ["apollo-link" :refer [ApolloLink execute makePromise]]
            ["apollo-link-http" :refer [HttpLink SetContextLink]]
            ["apollo-cache-inmemory" :refer [InMemoryCache IntrospectionFragmentMatcher]]
            ["apollo-link-batch-http" :refer [BatchHttpLink]]
            ["graphql-tag" :as gql]
            ["unfetch" :as fetch]
            ["promise-polyfill/src/polyfill"]

            [web3.constants :as constants]
            [web3.env :as env]
            [web3.i18n :as i]
            [web3.view :as v]
            [x-vec.graphql :as x]
            [web3.log :as log :include-macros true]
            [web3.utils :as utils]

            [reagent.core :as r]
            [re-frame.core :as rf]
            [re-frame-simple.db :as db]

            [goog.object :as gobj]
            [goog.functions :as gf]

            [clojure.string :as string]
            [clojure.string :as str]
            [web3.tracking.sentry :as sentry]
            [web3.auth.lazy-api :as lazy-auth]
            [applied-science.js-interop :as j]))

;; https://github.com/apollographql/apollo-client/blob/master/Upgrade.md#upgrading-from-custom-networkinteface-with-middleware--afterware

;; TODO
;; - request batching

(defn generate-headers
  "Creates a JS object with GraphQL headers for authentication and
  session context IDs for system observability"
  []
  (let [request-id (str (random-uuid))
        session-id (db/get-in [:session :id])
        version-id (versions/version-string)
        user-email (:email (lazy-auth/call-if-loaded :get-profile))
        token (lazy-auth/call-if-loaded :get-token)]
    {:authorization (some->> token (str "Bearer "))
     :x-centriq-client-name (db/get :centriq/client-name)
     :x-centriq-request-id request-id
     :x-centriq-session-id session-id
     :x-centriq-user-email user-email
     :x-centriq-client-app (:partner/id env/config)
     :x-centriq-version-id version-id}))

(defn with-auth
  [context]
  (assoc context :headers (generate-headers)))

(def auth-middleware (ApolloLink.
                      (fn [operation forward]
                        (.setContext operation (clj->js (with-auth {})))
                        (forward operation))))
(def sentry-middleware (ApolloLink.
                        (fn [operation forward]
                          (sentry/breadcrumb! {:message "Sending GraphQL API request"
                                               :category "GraphQL"
                                               :data {:operationName (gobj/get operation "operationName")
                                                      :request-id (-> (.getContext operation)
                                                                      (gobj/getValueByKeys "headers" "x-centriq-request-id"))
                                                      :variables (gobj/get operation "variables")}})
                          (forward operation))))

(def Client (doto
             (new ApolloClient #js {:link (-> auth-middleware
                                              (.concat sentry-middleware)
                                              (.concat (HttpLink. #js {:uri (env/config :GRAPHQL_API_URI)
                                                                       :fetch fetch})))
                                    :cache (InMemoryCache.
                                            #js {:fragmentMatcher (IntrospectionFragmentMatcher. #js {:introspectionQueryResultData constants/graphql-introspection-query-data})
                                                 :dataIdFromObject (fn [^js x]
                                                                     (when-not (or (nil? (.-__typename x))
                                                                                   (nil? (.-id x)))
                                                                       (str (.-__typename x) "-" (.-id x)))
                                                                     )})
                                    :fetch fetch
                                    :queryDeduplication true})
              (.initQueryManager)))

(defn parse-query-response
  "Returns data in Clojure data structures (with keywordized-keys),
  with additional ::graphql/- namespaced status values."
  [x]
  (merge (js->clj (.-data x) :keywordize-keys true)
         #:graphql {:loading? (.-loading x)
                    :partial? (.-partial x)
                    :stale? (.-stale x)
                    :error (.-error x)}))

(defn select-data
  "Return only the query data, without query metadata"
  [query-response]
  (reduce-kv (fn [m k _]
               (cond-> m
                       (= "graphql" (namespace k)) (dissoc k))) query-response query-response))

;; keep track of component<>query subscriptions
(defonce registry (volatile! {}))

(defn print-active-queries
  "Print each active graphql query, and the mounted components which depend on it."
  []
  (log/with-group-collapsed :debug "🔎 Active Queries"
                                   (->>
                                    (for [[component queries] (:by-component @registry)]
                                      (list (print-str component) \newline
                                            (for [[query variables] queries]
                                              (list "   " (print-str query) ", " (print-str variables) \newline))))
                                    (flatten)
                                    (apply str)
                                    (log/log :debug))))

(defn not-authed?
  "Returns true if error was caused by client not being authenticated"
  [error]
  (= 401 (some-> error
                 .-networkError
                 .-response
                 .-status)))

(defn parse-graphql-error
  "Return map with :message and :severity for graphql error"
  [error]
  (let [message (or (some-> error .-networkError .-message)
                    (some-> error .-message))]
    (case message
      "Failed to fetch"
      ;; TODO
      ;; try and detect whether the user has an internet connection or not.
      {:message (i/to-do "We can't connect to the server right now, please check your internet connection and try again.")
       :severity :low}
      (do
        (log/log :debug "Unhandled graphql error message: " message)
        {:message (cond-> message
                          (utils/some-str message) (str/replace "GraphQL error: " "Error: "))
         :severity :high}))))

(defn handle-graphql-error
  "Execute relevant auth side effects & write parsed error to `path` in db."
  [{:keys [db-path
           query
           variables]} error]

  (when (not-authed? error)
    (lazy-auth/call-if-loaded :start-flow :force-refresh))

  (rf/dispatch [:events/handle-error
                (merge
                 (parse-graphql-error error)
                 {:source :graphql/error
                  :error error
                  :debug-log {:path db-path
                              :query (str query)
                              :variables variables}
                  :db-path (conj db-path :graphql/error)})]))

(defn write-query-response! [query variables data]
  (db/dispatch [:update-in [query variables]
                merge (parse-query-response data)]))

(defn format-vars [variables]
  (reduce-kv (fn [m k v]
               (cond-> m
                       (not= "graphql" (when (keyword? k) (namespace k)))
                       (j/assoc! k (clj->js v))))
             #js{} variables))

(defn reframe-query-subscribe
  "Start re-frame graphql listener.

   Initiates graphql query, and writes results to `[<query> <variables>]` path in re-frame."
  [[query variables]]
  (log/println :debug "🙏 Subscribe" query)
  (v/silently
    (v/swap-silently! re-frame.db/app-db assoc-in [query variables :graphql/loading?] true))
  (let [^js observable
        (.watchQuery Client
                     #js {:query @query
                          :errorPolicy "all"
                          :fetchResults true
                          :variables (format-vars variables)})
        ^js subscription
        (.subscribe observable
                    #js {:next
                         (fn [x]
                           (write-query-response! query variables x)
                           (when (and (.-stale x)
                                      (:graphql/refetch-stale? variables true))
                             (.refetch observable (format-vars variables))))
                         :error
                         (fn [error]
                           (handle-graphql-error {:db-path [query variables]
                                                  :query query
                                                  :variables variables} error))})]
    (v/swap-silently! re-frame.db/app-db update-in [query variables] merge #:graphql {:query query
                                                                                      :variables variables
                                                                                      :observable observable
                                                                                      :subscription subscription})))

(defn once
  "Fetch a query once, call on-complete/on-error callbacks."
  [query variables {:keys [on-complete
                           on-error]}]
  (-> (.query Client
              #js {:query @query
                   :fetchResults true
                   :fetchPolicy "network-only"
                   :variables (format-vars variables)})
      (.then (fn [data]
               (when on-complete (on-complete data))))
      (.catch (fn [error]
                (when on-error
                  (on-error error))))))


(defn active-queries []
  (set (for [[_ queries] (:by-component @registry)
             [query variables] queries
             :let [result (db/get-in [query variables])]]
         result)))

(defn retry-errored-queries! []
  (doseq [{:keys [graphql/variables
                  graphql/query]} (filter :graphql/error (active-queries))]
    (once query variables {:on-complete (partial write-query-response! query variables)})))

(rf/reg-fx :graphql/retry-errored-queries retry-errored-queries!)

(defn reframe-query-unsubscribe
  "Stop re-frame graphql listener.

  This should be done when no more components on the page depend on the query."
  [[query variables :as path]]
  (log/println :debug "💤" query)
  (when-let [{:keys [graphql/subscription]} (db/get-in path)]
    (.unsubscribe subscription))
  (v/swap-silently! re-frame.db/app-db update path select-data))

(defn component-query-deregister
  "Deregister `component` from all paths.
  If no components are listening to path, stop syncing with re-frame."
  [component]
  (log/println :debug "💤" component)
  (when-let [paths (seq (get-in @registry [:by-component component]))]
    (doseq [path paths]
      (vswap! registry update-in [:by-query path] disj component)
      (when (empty? (get-in @registry [:by-query path]))
        (vswap! registry update :by-query dissoc path)
        (reframe-query-unsubscribe path))))
  (vswap! registry update :by-component dissoc component))

(defn component-query-register
  "Register `component` as listener on `path`. Idempotent.

  (a component keeps track of the subscriptions it listens to)"
  [component path]
  ;; (println :component-query-register component path)
  (v/on-unmount! component ::query-deregister component-query-deregister)
  (vreset! registry
           (-> @registry
               (update-in [:by-query path] (fnil conj #{}) component)
               (update-in [:by-component component] (fnil conj #{}) path))))

(defn invoke-aware-debounce*
  [f _ _ {:keys [debounce-ms]}]
  (gf/debounce f debounce-ms))

(def invoke-aware-debounce (memoize invoke-aware-debounce*))

(extend-type x/GraphQL
  IFn
  ;; allow a query to be called as a function, with variables
  ;; passed in as a map.

  ;; for mutations, on-completea and on-error callbacks as well as
  ;; a vec of queries under refetch (e.g. [q-root/me]) may be passed.
  (-invoke
    ([this] (this {} {}))
    ([this variables] (this variables {}))
    ([this variables options]
     (case (:operation this)
       ("query" "subscription")
       (if (lazy-auth/call-if-loaded :loading?)
         {:graphql/loading? true}
         (let [path [this variables]
               {:keys [debounce-ms]} options]

           (when (empty? (get-in @registry [:by-query path]))
             (let [rf-q-s (if-not debounce-ms
                            reframe-query-subscribe
                            (invoke-aware-debounce reframe-query-subscribe
                                                   this
                                                   1
                                                   options))]
              (rf-q-s path)))

           (when-let [component (r/current-component)]
             (component-query-register component path))

           ;; subscribe
           (db/get-in [this variables] {:graphql/loading? true})

           ;; read directly from app-db to ensure we get the latest value,
           ;; which was possibly modified in `reframe-query-subscribe`.
           ;; otherwise we can get an older cached version, "because subscriptions",
           ;; and this can cause flickering.
           (get-in (.-state re-frame.db/app-db) [this variables])))
       "mutation"
       (let [{:keys [component path on-complete on-error refetch]
              :or {refetch []}} options]
         (assert component ":component must be passed in options to mutations")
         (let [path (into [component :mutations] path)
               set-loading! (fn [loading?]
                              (rf/dispatch [:update-in path merge {:graphql/loading? loading?}]))]
           (set-loading! true)
           (-> (.mutate Client #js {:mutation @this
                                    :variables (format-vars variables)
                                    :refetchQueries (clj->js (mapv name refetch))})
               (.then
                (fn [^js payload]
                  (set-loading! false)
                  (when on-complete (on-complete payload))))
               (.catch
                (fn [e]
                  (set-loading! false)
                  (handle-graphql-error {:db-path path
                                         :query this
                                         :variables variables} e)
                  (when on-error (on-error e))))))))))
  IPrintWithWriter
  (-pr-writer [this writer opts]
    (-write writer (str "🔎 " (let [[_ ns name] (->> (first @(:form this))
                                                     (re-find #".*_([^_]+)__(.*)$"))]
                                (str ns "/" name)) ""))))

(defn mutation-state
  ([] (mutation-state (r/current-component)))
  ([component]
   (mutation-state component []))
  ([component path]
   (db/get-in (into [component :mutations] path))))

(defn clear-mutation-state!
  ([] (clear-mutation-state! (r/current-component)))
  ([component]
   (clear-mutation-state! component []))
  ([component path]
   (assert component (str "can't clear-mutation-state! for component: " component))
   (db/dispatch-sync [:assoc-in (into [component :mutations] path) nil])))

(defn local-assoc!
  "Assigns a new value to the `key` attribute on the entity of `id` in the local GraphQL cache.

   Type is inferred from `id`, or can be provided explicitly.

   Warning: this only affects the local store, and should not be used for mutation-related purposes.
   The motivating use-case is adding a `:new?` attribute to locally created entities, in order to
   render appropriate transitions."
  [gql-type id key val]
  (let [gql-type (name gql-type)]
    (.writeFragment Client #js {:id id
                                :fragment @(x/fn ^fragment get-key {:on gql-type} [] key)
                                :data (clj->js {key val
                                                :__typename gql-type})})))

(x/def ^fragment typename-fragment {:on "Entity"}
  :uuid
  :__typename)

(defn query-cache
  [q vars]
  (let [q-map {:query @q}
        vars-map {:variables vars}
        submit-map (if vars
                     (merge q-map vars-map)
                     q-map)]
   (.readQuery Client (clj->js submit-map))))


(defn local-typename
  "Returns __typename for graphql entity in local cache"
  [id]
  ;; TODO
  ;; problematic - sometimes seems to return null even when the thing exists in the local cache
  (some-> (.readFragment Client #js {:id id
                                     :fragment @typename-fragment})
          (gobj/get "__typename")))

(comment
 (local-assoc! "Asset" "5812f8c2-8a10-4e64-90f2-f6529e1ffe33" :nickname "A Better Fridge")
 (local-typename "5812f8c2-8a10-4e64-90f2-f6529e1ffe33"))
